import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Skeleton,
  TableSortLabel,
} from '@mui/material';
import { UserPerformance } from '../../../types/reporting';
import { reportingService } from '../../../services/reportingService';

interface UserPerformanceTableProps {
  data?: UserPerformance[];
  totals?: UserPerformance;
  loading?: boolean;
  selectedUserIds?: string[];
  onSelectionChange?: (userIds: string[]) => void;
  selectable?: boolean;
}

type SortField = 'userName' | 'appointmentsCount' | 'jobsCreated' | 'jobsConverted' | 'conversionRate' | 'totalRevenue' | 'averageJobValue';
type SortDirection = 'asc' | 'desc';

const UserPerformanceTable: React.FC<UserPerformanceTableProps> = ({
  data,
  totals,
  loading = false,
  selectedUserIds = [],
  onSelectionChange,
  selectable = false,
}) => {
  const [sortField, setSortField] = useState<SortField>('totalRevenue');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && data) {
      onSelectionChange?.(data.map((u) => u.userId));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onSelectionChange?.(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectionChange?.([...selectedUserIds, userId]);
    }
  };

  const sortedData = React.useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * direction;
      }
      return ((aValue as number) - (bValue as number)) * direction;
    });
  }, [data, sortField, sortDirection]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const allSelected = data && data.length > 0 && selectedUserIds.length === data.length;
  const someSelected = selectedUserIds.length > 0 && selectedUserIds.length < (data?.length || 0);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          User Performance
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'userName'}
                    direction={sortField === 'userName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('userName')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'appointmentsCount'}
                    direction={sortField === 'appointmentsCount' ? sortDirection : 'asc'}
                    onClick={() => handleSort('appointmentsCount')}
                  >
                    Appointments
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'jobsCreated'}
                    direction={sortField === 'jobsCreated' ? sortDirection : 'asc'}
                    onClick={() => handleSort('jobsCreated')}
                  >
                    Jobs Created
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'jobsConverted'}
                    direction={sortField === 'jobsConverted' ? sortDirection : 'asc'}
                    onClick={() => handleSort('jobsConverted')}
                  >
                    Converted
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'conversionRate'}
                    direction={sortField === 'conversionRate' ? sortDirection : 'asc'}
                    onClick={() => handleSort('conversionRate')}
                  >
                    Conv. Rate
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'totalRevenue'}
                    direction={sortField === 'totalRevenue' ? sortDirection : 'asc'}
                    onClick={() => handleSort('totalRevenue')}
                  >
                    Revenue
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'averageJobValue'}
                    direction={sortField === 'averageJobValue' ? sortDirection : 'asc'}
                    onClick={() => handleSort('averageJobValue')}
                  >
                    Avg Value
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((user) => (
                <TableRow
                  key={user.userId}
                  hover
                  selected={selectedUserIds.includes(user.userId)}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUserIds.includes(user.userId)}
                        onChange={() => handleSelectUser(user.userId)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{user.userName}</TableCell>
                  <TableCell align="right">{user.appointmentsCount}</TableCell>
                  <TableCell align="right">{user.jobsCreated}</TableCell>
                  <TableCell align="right">{user.jobsConverted}</TableCell>
                  <TableCell align="right">{user.conversionRate.toFixed(1)}%</TableCell>
                  <TableCell align="right">
                    {reportingService.formatCurrency(user.totalRevenue)}
                  </TableCell>
                  <TableCell align="right">
                    {reportingService.formatCurrency(user.averageJobValue)}
                  </TableCell>
                </TableRow>
              ))}
              {totals && (
                <TableRow sx={{ backgroundColor: 'action.hover', fontWeight: 600 }}>
                  {selectable && <TableCell padding="checkbox" />}
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {totals.appointmentsCount}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {totals.jobsCreated}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {totals.jobsConverted}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {totals.conversionRate.toFixed(1)}%
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {reportingService.formatCurrency(totals.totalRevenue)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {reportingService.formatCurrency(totals.averageJobValue)}
                  </TableCell>
                </TableRow>
              )}
              {sortedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={selectable ? 8 : 7} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No user data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default UserPerformanceTable;
